import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Briefcase, CheckCircle, XCircle, Eye, MapPin, DollarSign, Users, Calendar, Download, FileSpreadsheet, Search, Building2, Mail, Phone, GraduationCap, ChevronLeft, ChevronRight, Edit, Save, X } from 'lucide-react';
import api, { getBackendUrl, getAssetUrl, formatSalary } from '@/utils/api';
import ShareToWhatsAppButton from '@/components/ShareToWhatsAppButton';

const JobManagement = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [employerFilter, setEmployerFilter] = useState('all');
  const [employerSearch, setEmployerSearch] = useState('');
  const [showEmployerSuggestions, setShowEmployerSuggestions] = useState(false);
  const [filteredEmployers, setFilteredEmployers] = useState([]);
  const [selectedEmployerName, setSelectedEmployerName] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [approvedFromDate, setApprovedFromDate] = useState('');
  const [approvedToDate, setApprovedToDate] = useState('');
  const [employers, setEmployers] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const limit = 100;
  
  // Multi-select states
  const [selectedJobs, setSelectedJobs] = useState(new Set());
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  
  // Confirmation dialog states
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [pendingActionJobId, setPendingActionJobId] = useState(null);
  const [rejectionNotes, setRejectionNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, [statusFilter, employerFilter, fromDate, toDate, approvedFromDate, approvedToDate, currentPage]);

  useEffect(() => {
    fetchEmployersList();
  }, []);

  // Filter employers based on search input
  useEffect(() => {
    if (employerSearch.trim() && employers.length > 0) {
      const q = employerSearch.toLowerCase();
      const filtered = employers.filter(emp =>
        emp.company_name?.toLowerCase().includes(q) ||
        emp.name?.toLowerCase().includes(q) ||
        emp.mobile?.includes(employerSearch.trim()) ||
        emp.email?.toLowerCase().includes(q)
      );
      setFilteredEmployers(filtered.slice(0, 25)); // Show max 25 suggestions
      setShowEmployerSuggestions(true);
    } else {
      setFilteredEmployers([]);
      setShowEmployerSuggestions(false);
    }
  }, [employerSearch, employers]);

  const handleEmployerSelect = (emp) => {
    setEmployerFilter(emp.id);
    setSelectedEmployerName(emp.company_name || emp.name);
    setEmployerSearch('');
    setShowEmployerSuggestions(false);
  };

  const clearEmployerFilter = () => {
    setEmployerFilter('all');
    setSelectedEmployerName('');
    setEmployerSearch('');
  };

  useEffect(() => {
    fetchJobs();
  }, [statusFilter, employerFilter, fromDate, toDate, approvedFromDate, approvedToDate, currentPage]);

  useEffect(() => {
    fetchEmployersList();
  }, []);

  const fetchEmployersList = async () => {
    try {
      // Get all employers (not filtered by approval status) for the dropdown
      const response = await api.get('/admin/employers-list?status=all');
      setEmployers(response.data);
    } catch (error) {
      console.error('Failed to fetch employers list');
    }
  };

  const fetchJobs = async () => {
    try {
      setLoading(true);
      let url = '/admin/jobs';
      const params = [`page=${currentPage}`, `limit=${limit}`];
      if (statusFilter !== 'all') params.push(`status=${statusFilter}`);
      if (employerFilter !== 'all') params.push(`employer_id=${employerFilter}`);
      if (fromDate) params.push(`from_date=${fromDate}`);
      if (toDate) params.push(`to_date=${toDate}`);
      if (approvedFromDate) params.push(`approved_from_date=${approvedFromDate}`);
      if (approvedToDate) params.push(`approved_to_date=${approvedToDate}`);
      url += '?' + params.join('&');
      
      const response = await api.get(url);
      setJobs(response.data.data || []);
      setTotalPages(response.data.total_pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (jobId) => {
    setPendingActionJobId(jobId);
    setShowApproveDialog(true);
  };

  const confirmApprove = async () => {
    setActionLoading(true);
    try {
      await api.put(`/admin/jobs/${pendingActionJobId}/approve`);
      toast.success('Job approved successfully');
      fetchJobs();
      setShowDetailDialog(false);
      setShowApproveDialog(false);
      setPendingActionJobId(null);
    } catch (error) {
      toast.error('Failed to approve job');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (jobId) => {
    setPendingActionJobId(jobId);
    setRejectionNotes('');
    setShowRejectDialog(true);
  };

  const confirmReject = async () => {
    if (!rejectionNotes.trim()) {
      toast.error('Rejection reason is required');
      return;
    }
    setActionLoading(true);
    try {
      await api.put(`/admin/jobs/${pendingActionJobId}/reject`, { rejection_notes: rejectionNotes.trim() });
      toast.success('Job rejected');
      fetchJobs();
      setShowDetailDialog(false);
      setShowRejectDialog(false);
      setPendingActionJobId(null);
      setRejectionNotes('');
    } catch (error) {
      toast.error('Failed to reject job');
    } finally {
      setActionLoading(false);
    }
  };

  // Multi-select handlers
  const toggleJobSelection = (jobId) => {
    const newSelected = new Set(selectedJobs);
    if (newSelected.has(jobId)) {
      newSelected.delete(jobId);
    } else {
      newSelected.add(jobId);
    }
    setSelectedJobs(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedJobs.size === jobs.length) {
      setSelectedJobs(new Set());
    } else {
      setSelectedJobs(new Set(jobs.map(j => j.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedJobs.size === 0) return;
    
    setBulkDeleteLoading(true);
    try {
      const jobIds = Array.from(selectedJobs);
      await api.post('/admin/jobs/bulk-delete', { job_ids: jobIds });
      toast.success(`Successfully deleted ${jobIds.length} jobs`);
      setSelectedJobs(new Set());
      setShowBulkDeleteDialog(false);
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete jobs');
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const startEditing = () => {
    setEditFormData({
      job_title: selectedJob.job_title || '',
      job_type: selectedJob.job_type || 'Permanent',
      short_info: selectedJob.short_info || '',
      description: selectedJob.description || '',
      min_experience: selectedJob.min_experience || 0,
      max_experience: selectedJob.max_experience || 0,
      min_salary: selectedJob.min_salary || 0,
      max_salary: selectedJob.max_salary || 0,
      salary_type: selectedJob.salary_type || 'numeric',
      vacancies: selectedJob.vacancies || 1,
      last_date: selectedJob.last_date || '',
      contact_number: selectedJob.contact_number || '',
      email: selectedJob.email || '',
      min_education: Array.isArray(selectedJob.min_education) ? selectedJob.min_education : (selectedJob.min_education ? [selectedJob.min_education] : []),
      course_subject: selectedJob.course_subject || '',
      gender: selectedJob.gender || [],
      locations: selectedJob.locations || [],
      skills: selectedJob.skills || [],
      min_age: selectedJob.min_age || '',
      max_age: selectedJob.max_age || '',
      publish_on: selectedJob.publish_on || ['job_portal']
    });
    setIsEditing(true);
  };

  // Education options for multi-select
  const educationOptions = ['10th', '12th', 'ITI', 'Diploma', 'UG Degree', 'PG Degree', 'UG Engineering', 'PG Engineering', 'Others'];

  // Location states for edit
  const [locationSearchInput, setLocationSearchInput] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);

  const handleEducationChange = (education, checked) => {
    if (checked) {
      setEditFormData({ ...editFormData, min_education: [...editFormData.min_education, education] });
    } else {
      setEditFormData({ ...editFormData, min_education: editFormData.min_education.filter(e => e !== education) });
    }
  };

  // Location search function
  const searchLocations = async (query) => {
    if (query.length < 2) {
      setLocationSuggestions([]);
      return;
    }
    try {
      const response = await api.get(`/location-master/search?q=${encodeURIComponent(query)}&limit=10`);
      setLocationSuggestions(response.data || []);
      setShowLocationSuggestions(true);
    } catch (error) {
      console.error('Error searching locations:', error);
    }
  };

  const addLocation = (location) => {
    const newLoc = {
      state: location.state,
      district: location.district,
      city: location.city || location.district
    };
    // Check if already exists
    const exists = editFormData.locations?.some(
      l => l.state === newLoc.state && l.district === newLoc.district
    );
    if (!exists) {
      setEditFormData({
        ...editFormData,
        locations: [...(editFormData.locations || []), newLoc]
      });
    }
    setLocationSearchInput('');
    setShowLocationSuggestions(false);
  };

  const removeLocation = (index) => {
    setEditFormData({
      ...editFormData,
      locations: editFormData.locations.filter((_, i) => i !== index)
    });
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditFormData(null);
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/admin/jobs/${selectedJob.id}/edit`, editFormData);
      toast.success('Job updated successfully');
      setIsEditing(false);
      fetchJobs();
      // Refresh the selected job data
      const response = await api.get(`/admin/jobs/${selectedJob.id}`);
      setSelectedJob(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update job');
    }
  };

  const viewDetails = (job) => {
    setSelectedJob(job);
    setIsEditing(false);
    setEditFormData(null);
    setShowDetailDialog(true);
  };

  const handleExport = async (format) => {
    try {
      let url = `/admin/jobs/export?format=${format}`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      if (employerFilter !== 'all') url += `&employer_id=${employerFilter}`;
      if (fromDate) url += `&from_date=${fromDate}`;
      if (toDate) url += `&to_date=${toDate}`;
      if (approvedFromDate) url += `&approved_from_date=${approvedFromDate}`;
      if (approvedToDate) url += `&approved_to_date=${approvedToDate}`;
      
      window.open(getBackendUrl() + '/api' + url, '_blank');
      toast.success(`Exporting to ${format.toUpperCase()}...`);
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setEmployerFilter('all');
    setSelectedEmployerName('');
    setEmployerSearch('');
    setFromDate('');
    setToDate('');
    setApprovedFromDate('');
    setApprovedToDate('');
    setCurrentPage(1);
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return getBackendUrl() + path;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusBadge = (job) => {
    // First check if it's a prime employer job
    const isPrime = job.is_prime_employer_job;
    
    // For prime employer jobs, they are typically auto-approved
    // Check the actual status and active fields
    const status = job.status;
    const isActive = job.active !== false;
    
    // If active is false, show as Inactive regardless of status
    if (!isActive) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">Inactive</span>;
    }
    
    // Show status based on the status field
    if (status === 'approved' || (isPrime && isActive)) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Active</span>;
    }
    if (status === 'pending') {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Pending</span>;
    }
    if (status === 'rejected') {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Rejected</span>;
    }
    // Default to active if active=true and no specific status
    return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Active</span>;
  };
  
  const getEmployerTypeBadge = (job) => {
    if (job.is_prime_employer_job) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Prime</span>;
    }
    return <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Regular</span>;
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>Job Management</h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('csv')}
            className="text-slate-600"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('xlsx')}
            className="text-slate-600"
            data-testid="export-excel-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-1" />
            Excel
          </Button>
        </div>
      </div>

      {/* Bulk Actions Bar - appears when jobs are selected */}
      {selectedJobs.size > 0 && (
        <Card className="p-3 mb-4 border-red-200 bg-red-50">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">
              {selectedJobs.size} job{selectedJobs.size > 1 ? 's' : ''} selected
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedJobs(new Set())}
              >
                Clear Selection
              </Button>
              <Button
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => setShowBulkDeleteDialog(true)}
                data-testid="bulk-delete-btn"
              >
                <XCircle className="w-4 h-4 mr-1" />
                Delete Selected
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card className="p-4 mb-6 border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Posted From</label>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              data-testid="from-date-input"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Posted To</label>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              data-testid="to-date-input"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Approved From</label>
            <Input
              type="date"
              value={approvedFromDate}
              onChange={(e) => setApprovedFromDate(e.target.value)}
              data-testid="approved-from-date-input"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Approved To</label>
            <Input
              type="date"
              value={approvedToDate}
              onChange={(e) => setApprovedToDate(e.target.value)}
              data-testid="approved-to-date-input"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Status</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="status-filter-select">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="relative">
            <label className="block text-xs text-slate-500 mb-1">Employer</label>
            {selectedEmployerName ? (
              <div className="flex items-center gap-2 h-10 px-3 border border-slate-300 rounded-md bg-slate-50">
                <Building2 className="w-4 h-4 text-slate-400" />
                <span className="text-sm flex-1 truncate">{selectedEmployerName}</span>
                <button 
                  onClick={clearEmployerFilter}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search employer..."
                  value={employerSearch}
                  onChange={(e) => setEmployerSearch(e.target.value)}
                  onFocus={() => employerSearch && setShowEmployerSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowEmployerSuggestions(false), 200)}
                  className="pl-9"
                  data-testid="employer-search-input"
                />
              </div>
            )}
            {showEmployerSuggestions && filteredEmployers.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                {filteredEmployers.map((emp) => (
                  <div
                    key={emp.id}
                    className="px-3 py-2 cursor-pointer hover:bg-slate-100 flex items-center gap-2"
                    onMouseDown={() => handleEmployerSelect(emp)}
                  >
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{emp.company_name || 'N/A'}</p>
                      <p className="text-xs text-slate-500 truncate">{emp.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {showEmployerSuggestions && employerSearch && filteredEmployers.length === 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg p-3 text-center text-sm text-slate-500">
                No employers found
              </div>
            )}
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              onClick={clearFilters}
              className="w-full"
              data-testid="clear-filters-btn"
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </Card>

      {/* Jobs Grid Table */}
      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-10 text-center">
                  <Checkbox 
                    checked={jobs.length > 0 && selectedJobs.size === jobs.length}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                </TableHead>
                <TableHead className="w-12 text-center font-semibold">Sl.No</TableHead>
                <TableHead className="font-semibold">Serial No</TableHead>
                <TableHead className="font-semibold">Date of Post</TableHead>
                <TableHead className="font-semibold">Approved Date</TableHead>
                <TableHead className="font-semibold">Job Title</TableHead>
                <TableHead className="font-semibold">Locations</TableHead>
                <TableHead className="font-semibold">Employer</TableHead>
                <TableHead className="font-semibold">Posted By</TableHead>
                <TableHead className="font-semibold">Emp Status</TableHead>
                <TableHead className="text-center font-semibold">Type</TableHead>
                <TableHead className="font-semibold">Email</TableHead>
                <TableHead className="font-semibold">Mobile</TableHead>
                <TableHead className="font-semibold">Education</TableHead>
                <TableHead className="font-semibold">Experience</TableHead>
                <TableHead className="text-center font-semibold">Age Limit</TableHead>
                <TableHead className="font-semibold">Skills</TableHead>
                <TableHead className="text-center font-semibold">Vacancies</TableHead>
                <TableHead className="font-semibold">Salary Range</TableHead>
                <TableHead className="font-semibold">Expiry Date</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="text-center font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={21} className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-2"></div>
                    <p className="text-slate-600">Loading jobs...</p>
                  </TableCell>
                </TableRow>
              ) : jobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={21} className="text-center py-12">
                    <Briefcase className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-600">No jobs found</p>
                  </TableCell>
                </TableRow>
              ) : (
                jobs.map((job, index) => (
                  <TableRow key={job.id} className={`hover:bg-slate-50 ${selectedJobs.has(job.id) ? 'bg-teal-50' : ''}`}>
                    <TableCell className="text-center">
                      <Checkbox 
                        checked={selectedJobs.has(job.id)}
                        onCheckedChange={() => toggleJobSelection(job.id)}
                        data-testid={`select-job-${job.id}`}
                      />
                    </TableCell>
                    <TableCell className="text-center font-medium">{index + 1}</TableCell>
                    <TableCell>
                      {job.serial_number ? (
                        <span className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-mono font-medium">
                          {job.serial_number}
                        </span>
                      ) : (
                        <span className="text-slate-400 text-xs">-</span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{formatDateTime(job.created_at)}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      {job.approved_at ? (
                        <span className="text-emerald-700">{formatDateTime(job.approved_at)}</span>
                      ) : (
                        <span className="text-slate-400 text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-slate-900 max-w-[150px] truncate" title={job.job_title}>
                        {job.job_title}
                      </div>
                      <div className="text-xs text-slate-500">{job.job_type}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-[120px]">
                        {job.locations?.slice(0, 2).map((loc, i) => (
                          <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-100 rounded text-xs">
                            {loc.city && loc.city !== loc.district ? `${loc.city}, ${loc.district}` : (loc.city || loc.district)}
                          </span>
                        ))}
                        {job.locations?.length > 2 && (
                          <span className="text-xs text-slate-500">+{job.locations.length - 2}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-slate-900 max-w-[120px] truncate" title={job.company_name}>
                        {job.company_name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-slate-700 max-w-[100px] truncate" title={job.posted_by_name || 'N/A'}>
                        {job.posted_by_name || <span className="text-slate-400">N/A</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      {job.employer_approval_status === 'approved' ? (
                        <span className="inline-flex items-center px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium">Approved</span>
                      ) : job.employer_approval_status === 'pending' ? (
                        <span className="inline-flex items-center px-2 py-1 bg-amber-50 text-amber-700 rounded-full text-xs font-medium">Pending</span>
                      ) : job.employer_approval_status === 'rejected' ? (
                        <span className="inline-flex items-center px-2 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium">Rejected</span>
                      ) : (
                        <span className="text-slate-400 text-xs">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {getEmployerTypeBadge(job)}
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm max-w-[120px] truncate">
                      {job.employer_email || job.email || 'N/A'}
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm">
                      {job.employer_mobile || job.contact_number || 'N/A'}
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm max-w-[100px] truncate" title={Array.isArray(job.min_education) ? job.min_education.join(', ') : job.min_education}>
                      {job.min_education ? (Array.isArray(job.min_education) ? job.min_education.join(', ') : job.min_education) : 'N/A'}
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm whitespace-nowrap">
                      {job.min_experience}-{job.max_experience} yrs
                    </TableCell>
                    <TableCell className="text-center">
                      {(job.min_age || job.max_age) ? (
                        <span className="inline-flex items-center px-2 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium">
                          {job.min_age && job.max_age 
                            ? `${job.min_age}-${job.max_age} yrs`
                            : job.min_age 
                              ? `Min ${job.min_age} yrs`
                              : `Max ${job.max_age} yrs`}
                        </span>
                      ) : (
                        <span className="text-slate-400 text-xs">N/A</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-[150px]">
                        {job.skills?.slice(0, 2).map((skill, i) => (
                          <span key={i} className="px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-xs truncate max-w-[70px]" title={skill}>
                            {skill.length > 12 ? skill.substring(0, 12) + '...' : skill}
                          </span>
                        ))}
                        {job.skills?.length > 2 && (
                          <span className="text-xs text-slate-500">+{job.skills.length - 2}</span>
                        )}
                        {(!job.skills || job.skills.length === 0) && (
                          <span className="text-slate-400 text-xs">N/A</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                        {job.vacancies}
                      </span>
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm whitespace-nowrap">
                      {formatSalary(job)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{formatDate(job.last_date)}</TableCell>
                    <TableCell>{getStatusBadge(job)}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewDetails(job)}
                          data-testid={`view-job-${job.id}`}
                          className="text-slate-600 hover:text-teal-600"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {job.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleApprove(job.id)}
                              data-testid={`approve-job-${job.id}`}
                              className="bg-green-600 hover:bg-green-700 text-white h-8 w-8 p-0 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={job.employer_approval_status !== 'approved' ? 'Employer must be approved first' : 'Approve'}
                              disabled={job.employer_approval_status !== 'approved'}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleReject(job.id)}
                              data-testid={`reject-job-${job.id}`}
                              className="bg-red-600 hover:bg-red-700 text-white h-8 w-8 p-0"
                              title="Reject"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {(job.status === 'approved' || (job.is_prime_employer_job && job.active !== false)) && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleReject(job.id)}
                              data-testid={`reject-approved-job-${job.id}`}
                              className="bg-red-600 hover:bg-red-700 text-white h-8 w-8 p-0"
                              title="Reject Job"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                            <ShareToWhatsAppButton job={job} />
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
            <div className="text-sm text-slate-600">
              Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, total)} of {total} jobs
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              <span className="text-sm text-slate-600 px-2">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Dialog open={showDetailDialog} onOpenChange={(open) => { setShowDetailDialog(open); if (!open) cancelEditing(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{isEditing ? 'Edit Job' : 'Job Details'}</span>
              {selectedJob && !isEditing && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={startEditing}
                  className="text-blue-600 border-blue-300 hover:bg-blue-50"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Job
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedJob && !isEditing && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                {selectedJob.company_logo ? (
                  <img src={getImageUrl(selectedJob.company_logo)} alt="Company" className="w-20 h-20 rounded-lg object-cover border border-slate-200" />
                ) : (
                  <div className="w-20 h-20 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Briefcase className="w-10 h-10 text-blue-600" />
                  </div>
                )}
                <div>
                  <h3 className="text-2xl font-bold text-slate-900">{selectedJob.job_title}</h3>
                  <p className="text-lg text-slate-600">{selectedJob.company_name}</p>
                  <div className="mt-2 flex gap-2">
                    {getStatusBadge(selectedJob.status)}
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                      {selectedJob.job_type}
                    </span>
                    {selectedJob.edited_by_admin && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                        Edited by Admin
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-semibold text-slate-900 mb-2">Short Description</h4>
                <p className="text-sm text-slate-700">{selectedJob.short_info}</p>
              </div>

              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Full Description</h4>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedJob.description}</p>
              </div>

              {selectedJob.skills && selectedJob.skills.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Required Skills</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedJob.skills.map((skill, idx) => (
                      <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Job Details</h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>Experience:</strong> {selectedJob.min_experience}-{selectedJob.max_experience} years</p>
                    <p><strong>Salary:</strong> {formatSalary(selectedJob)}</p>
                    <p><strong>Vacancies:</strong> {selectedJob.vacancies}</p>
                    <p><strong>Gender:</strong> {selectedJob.gender?.join(', ') || 'All'}</p>
                    {(selectedJob.min_age || selectedJob.max_age) && (
                      <p><strong>Age Limit:</strong> {
                        selectedJob.min_age && selectedJob.max_age 
                          ? `${selectedJob.min_age}-${selectedJob.max_age} years`
                          : selectedJob.min_age 
                            ? `Min ${selectedJob.min_age} years`
                            : `Max ${selectedJob.max_age} years`
                      }</p>
                    )}
                    {selectedJob.min_education && (
                      <p><strong>Education:</strong> {Array.isArray(selectedJob.min_education) ? selectedJob.min_education.join(', ') : selectedJob.min_education}</p>
                    )}
                    {selectedJob.course_subject && (
                      <p><strong>Course/Subject:</strong> {selectedJob.course_subject}</p>
                    )}
                    <p><strong>Last Date:</strong> {formatDate(selectedJob.last_date)}</p>
                    <p><strong>Posted:</strong> {formatDate(selectedJob.created_at)}</p>
                    <p><strong>Posted By:</strong> {selectedJob.posted_by_name || 'N/A'}</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Contact Information</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span>{selectedJob.contact_number}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <span>{selectedJob.email}</span>
                    </div>
                  </div>
                  
                  <h4 className="font-semibold text-slate-900 mb-2 mt-4">Employer Contact</h4>
                  <div className="space-y-2 text-sm">
                    {selectedJob.employer_mobile && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <span>{selectedJob.employer_mobile}</span>
                      </div>
                    )}
                    {selectedJob.employer_email && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <span>{selectedJob.employer_email}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {selectedJob.locations && selectedJob.locations.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Locations</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedJob.locations.map((loc, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
                        <MapPin className="w-3 h-3" />
                        {loc.city && loc.city !== loc.district ? `${loc.city}, ${loc.district}` : (loc.city || loc.district)}, {loc.state}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>Applications:</strong> {selectedJob.application_count || 0} candidates have applied to this job
                </p>
              </div>

              {/* Publishing Options Display */}
              {selectedJob.publish_on && selectedJob.publish_on.length > 0 && (
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-sm text-purple-700 font-medium mb-2">Publishing Options:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedJob.publish_on.includes('job_portal') && (
                      <span className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                        Job Portal
                      </span>
                    )}
                    {selectedJob.publish_on.includes('youtube') && (
                      <span className="inline-flex items-center px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">
                        YouTube
                      </span>
                    )}
                    {selectedJob.publish_on.includes('whatsapp') && (
                      <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                        WhatsApp
                      </span>
                    )}
                  </div>
                </div>
              )}

              {selectedJob.status === 'pending' && (
                <div className="flex gap-3 pt-4 border-t border-slate-200">
                  <Button
                    onClick={() => handleApprove(selectedJob.id)}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve Job
                  </Button>
                  <Button
                    onClick={() => handleReject(selectedJob.id)}
                    variant="outline"
                    className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject Job
                  </Button>
                </div>
              )}

              {(selectedJob.status === 'approved' || (selectedJob.is_prime_employer_job && selectedJob.active !== false)) && (
                <div className="flex gap-3 pt-4 border-t border-slate-200">
                  <Button
                    onClick={() => handleReject(selectedJob.id)}
                    variant="outline"
                    className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject This Job
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Edit Form */}
          {selectedJob && isEditing && editFormData && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Job Title *</label>
                  <Input
                    value={editFormData.job_title}
                    onChange={(e) => setEditFormData({...editFormData, job_title: e.target.value})}
                    placeholder="Job Title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Job Type</label>
                  <Select value={editFormData.job_type} onValueChange={(v) => setEditFormData({...editFormData, job_type: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Permanent">Permanent</SelectItem>
                      <SelectItem value="Temporary">Temporary</SelectItem>
                      <SelectItem value="Contract">Contract</SelectItem>
                      <SelectItem value="Internship">Internship</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Short Description</label>
                <Textarea
                  value={editFormData.short_info}
                  onChange={(e) => setEditFormData({...editFormData, short_info: e.target.value})}
                  placeholder="Brief description"
                  rows={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Full Description</label>
                <Textarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                  placeholder="Detailed job description"
                  rows={4}
                />
              </div>

              <div className="grid md:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Min Experience</label>
                  <Input
                    type="number"
                    value={editFormData.min_experience}
                    onChange={(e) => setEditFormData({...editFormData, min_experience: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Max Experience</label>
                  <Input
                    type="number"
                    value={editFormData.max_experience}
                    onChange={(e) => setEditFormData({...editFormData, max_experience: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Min Salary</label>
                  <Input
                    type="number"
                    value={editFormData.min_salary}
                    onChange={(e) => setEditFormData({...editFormData, min_salary: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Max Salary</label>
                  <Input
                    type="number"
                    value={editFormData.max_salary}
                    onChange={(e) => setEditFormData({...editFormData, max_salary: parseInt(e.target.value) || 0})}
                    disabled={editFormData.salary_type !== 'numeric' && editFormData.salary_type !== 'numeric_range'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Salary Type</label>
                  <Select 
                    value={editFormData.salary_type || 'numeric'} 
                    onValueChange={(v) => setEditFormData({...editFormData, salary_type: v, max_salary: v === 'numeric' || v === 'numeric_range' ? editFormData.max_salary : 0})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="numeric">Numeric Range</SelectItem>
                      <SelectItem value="as_per_norms">As Per Norms</SelectItem>
                      <SelectItem value="as_per_experience">As Per Experience</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Vacancies</label>
                  <Input
                    type="number"
                    value={editFormData.vacancies}
                    onChange={(e) => setEditFormData({...editFormData, vacancies: parseInt(e.target.value) || 1})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Contact Number</label>
                  <Input
                    type="tel"
                    value={editFormData.contact_number}
                    onChange={(e) => setEditFormData({...editFormData, contact_number: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                    placeholder="10-digit mobile number"
                    maxLength={10}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <Input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                    placeholder="Email"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Last Date to Apply</label>
                  <Input
                    type="date"
                    value={editFormData.last_date}
                    onChange={(e) => setEditFormData({...editFormData, last_date: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Course/Subject</label>
                  <Input
                    value={editFormData.course_subject || ''}
                    onChange={(e) => setEditFormData({...editFormData, course_subject: e.target.value})}
                    placeholder="e.g., Computer Science, Mechanical Engineering"
                  />
                </div>
              </div>

              {/* Education Qualification Multi-Select */}
              <div>
                <label className="block text-sm font-medium mb-2">Education Qualification</label>
                <div className="flex flex-wrap gap-3 p-3 border border-slate-200 rounded-lg bg-slate-50">
                  {educationOptions.map((edu) => (
                    <label key={edu} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={editFormData.min_education?.includes(edu)}
                        onCheckedChange={(checked) => handleEducationChange(edu, checked)}
                      />
                      <span>{edu}</span>
                    </label>
                  ))}
                </div>
                {editFormData.min_education?.length > 0 && (
                  <p className="text-xs text-teal-600 mt-1">Selected: {editFormData.min_education.join(', ')}</p>
                )}
              </div>

              {/* Skills */}
              <div>
                <label className="block text-sm font-medium mb-1">Skills</label>
                <Input
                  value={Array.isArray(editFormData.skills) ? editFormData.skills.join(', ') : editFormData.skills}
                  onChange={(e) => setEditFormData({...editFormData, skills: e.target.value.split(',').map(s => s.trim()).filter(s => s)})}
                  placeholder="Enter skills separated by commas (e.g., Java, Python, SQL)"
                />
                <p className="text-xs text-slate-500 mt-1">Separate multiple skills with commas</p>
              </div>

              {/* Job Locations */}
              <div>
                <label className="block text-sm font-medium mb-1">Job Locations</label>
                <div className="relative">
                  <Input
                    value={locationSearchInput}
                    onChange={(e) => {
                      setLocationSearchInput(e.target.value);
                      searchLocations(e.target.value);
                    }}
                    placeholder="Search and add locations (e.g., Chennai, Coimbatore)"
                    onFocus={() => locationSuggestions.length > 0 && setShowLocationSuggestions(true)}
                  />
                  {showLocationSuggestions && locationSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {locationSuggestions.map((loc, idx) => (
                        <div
                          key={idx}
                          className="px-3 py-2 hover:bg-teal-50 cursor-pointer text-sm flex items-center gap-2"
                          onClick={() => addLocation(loc)}
                        >
                          <MapPin className="w-4 h-4 text-slate-400" />
                          <span>{loc.state} / {loc.district}{loc.city && loc.city !== loc.district ? ` / ${loc.city}` : ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {editFormData.locations?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {editFormData.locations.map((loc, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-teal-50 text-teal-700 rounded-full text-xs">
                        <MapPin className="w-3 h-3" />
                        {loc.state} / {loc.district || loc.city}
                        <button
                          type="button"
                          onClick={() => removeLocation(idx)}
                          className="ml-1 text-teal-500 hover:text-red-500"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {(!editFormData.locations || editFormData.locations.length === 0) && (
                  <p className="text-xs text-orange-500 mt-1">No locations added yet</p>
                )}
              </div>

              {/* Gender & Age */}
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Gender</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {['Male', 'Female', 'Other'].map((g) => (
                      <label key={g} className="flex items-center gap-1 text-sm">
                        <input
                          type="checkbox"
                          checked={editFormData.gender?.includes(g)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setEditFormData({...editFormData, gender: [...(editFormData.gender || []), g]});
                            } else {
                              setEditFormData({...editFormData, gender: editFormData.gender.filter(x => x !== g)});
                            }
                          }}
                          className="rounded border-slate-300"
                        />
                        {g}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Min Age</label>
                  <Input
                    type="number"
                    value={editFormData.min_age}
                    onChange={(e) => setEditFormData({...editFormData, min_age: e.target.value ? parseInt(e.target.value) : ''})}
                    placeholder="e.g., 18"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Max Age</label>
                  <Input
                    type="number"
                    value={editFormData.max_age}
                    onChange={(e) => setEditFormData({...editFormData, max_age: e.target.value ? parseInt(e.target.value) : ''})}
                    placeholder="e.g., 35"
                  />
                </div>
              </div>

              {/* Locations Display */}
              <div>
                <label className="block text-sm font-medium mb-1">Locations</label>
                <div className="flex flex-wrap gap-2 p-2 bg-slate-50 rounded-md min-h-[40px]">
                  {editFormData.locations?.length > 0 ? (
                    editFormData.locations.map((loc, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-700 rounded text-xs">
                        {loc.city && loc.city !== loc.district ? `${loc.city}, ${loc.district}` : loc.district}, {loc.state}
                        <button
                          type="button"
                          onClick={() => setEditFormData({
                            ...editFormData,
                            locations: editFormData.locations.filter((_, i) => i !== idx)
                          })}
                          className="hover:text-teal-900"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))
                  ) : (
                    <span className="text-slate-400 text-sm">No locations added</span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-1">Location editing coming soon. Remove unwanted locations above.</p>
              </div>

              {/* Publishing Options */}
              <div>
                <label className="block text-sm font-medium mb-2">Publish Job On</label>
                <div className="flex flex-wrap gap-4 p-3 border border-slate-200 rounded-lg bg-slate-50">
                  {[
                    { value: 'job_portal', label: 'Job Portal (VVVSI.com)' },
                    { value: 'whatsapp', label: 'WhatsApp' },
                    { value: 'youtube', label: 'YouTube' }
                  ].map((option) => (
                    <label key={option.value} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editFormData.publish_on?.includes(option.value)}
                        onChange={(e) => {
                          const current = editFormData.publish_on || [];
                          if (e.target.checked) {
                            setEditFormData({...editFormData, publish_on: [...current, option.value]});
                          } else {
                            setEditFormData({...editFormData, publish_on: current.filter(v => v !== option.value)});
                          }
                        }}
                        className="rounded border-slate-300"
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t border-slate-200">
                <Button
                  onClick={handleSaveEdit}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
                <Button
                  onClick={cancelEditing}
                  variant="outline"
                  className="flex-1"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approve Confirmation Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-5 h-5" />
              Confirm Approval
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-700">
              Are you sure you want to <strong>approve</strong> this job posting?
            </p>
            <p className="text-sm text-slate-500 mt-2">
              Once approved, the job will be visible to job seekers and they can apply for it.
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowApproveDialog(false)} className="flex-1" disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={confirmApprove} className="flex-1 bg-green-600 hover:bg-green-700" disabled={actionLoading}>
              {actionLoading ? 'Approving...' : 'Yes, Approve'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Confirmation Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Reject Job
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-slate-700">
              Please provide a reason for rejecting this job posting.
            </p>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <Textarea
                value={rejectionNotes}
                onChange={(e) => setRejectionNotes(e.target.value)}
                placeholder="Enter the reason for rejection (this will be visible to the employer)"
                rows={3}
                className="w-full"
              />
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700">
                <strong>Note:</strong> This reason will be visible to the employer who posted this job.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowRejectDialog(false)} className="flex-1" disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={confirmReject} className="flex-1 bg-red-600 hover:bg-red-700" disabled={actionLoading || !rejectionNotes.trim()}>
              {actionLoading ? 'Rejecting...' : 'Reject Job'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Delete {selectedJobs.size} Jobs
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-slate-700">
              Are you sure you want to delete <strong>{selectedJobs.size}</strong> selected job{selectedJobs.size > 1 ? 's' : ''}?
            </p>
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">
                <strong>Warning:</strong> This action cannot be undone. All associated applications will also be deleted.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowBulkDeleteDialog(false)} className="flex-1" disabled={bulkDeleteLoading}>
              Cancel
            </Button>
            <Button onClick={handleBulkDelete} className="flex-1 bg-red-600 hover:bg-red-700" disabled={bulkDeleteLoading}>
              {bulkDeleteLoading ? 'Deleting...' : `Delete ${selectedJobs.size} Jobs`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobManagement;
